import { motion } from "framer-motion";
import styled from "styled-components";

export const DialogView = styled( motion.dialog )`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: fixed;
  inset: 0;
  z-index: 100;
  width: 100%;
  height: 100%;
  border: none;
  background-color: ${({ theme }) => theme.glass.background };
  backdrop-filter: ${({ theme }) => theme.glass.blur };
  transition: all .2s;

  &::backdrop {
    background: transparent;
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.xxx_small }px;
  color: ${({ theme }) => theme.colors.text.default };
  gap: ${({ theme }) => theme.spacing.xxx_medium }px;
  background-color: ${({ theme }) => theme.glass.elevated };
  border-bottom: 1px solid ${({ theme }) => theme.glass.border };
  backdrop-filter: ${({ theme }) => theme.glass.blur };

  & > div.avatar-view {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xx_small }px;
  }

  & > div.actions {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: flex-end;
    flex-direction: row;
  }

  & > div.avatar-view > div {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.x_small }px;
  }

  & > div.avatar-view > div > span:first-of-type {
    min-height: ${({ theme }) => theme.font_size.xx_small }px;
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
    line-height: ${({ theme }) => theme.font_size.xx_small }px;
    font-weight: 500;
  }

  & > div.avatar-view > div > span:last-of-type {
    min-height: ${({ theme }) => theme.font_size.x_small }px;
    font-size: ${({ theme }) => theme.font_size.x_small }px;
    line-height: ${({ theme }) => theme.font_size.x_small }px;
    font-weight: 300;
  }
`;

export const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  position: relative;
  height: calc( 100% - 64px );
  width: 100%;
  padding: ${({ theme }) => theme.spacing.xx_small }px;
  overflow: hidden;

  & > iframe {
    border: none;
    min-width: 872px;
  }

  & a.download-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 1px solid transparent;
    border-radius: ${({ theme }) => theme.border_radius.round }px;
    color: ${({ theme }) => theme.colors.text.default };
    text-decoration: none;
  }

  & a.download-button:hover {
    background-color: ${({ theme }) => theme.colors.selected.default };
  }
`;

export const ImagePreview = styled.div<{
  $zoom: number;
  $panX: number;
  $panY: number;
  $isDragging: boolean;
}>`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  cursor: ${({ $isDragging, $zoom }) => $zoom > 1 ? ( $isDragging ? "grabbing" : "grab" ) : "zoom-in" };
  touch-action: ${({ $zoom }) => $zoom > 1 ? "none" : "auto" };

  & > img {
    flex: 0 0 auto;
    max-width: ${({ $zoom }) => $zoom > 1 ? "none" : "100%" };
    max-height: ${({ $zoom }) => $zoom > 1 ? "none" : "100%" };
    width: ${({ $zoom }) => $zoom * 100 }%;
    height: auto;
    object-fit: contain;
    transform: ${({ $panX, $panY }) => `translate3d( ${ $panX }px, ${ $panY }px, 0 )`};
    transition: ${({ $isDragging }) => $isDragging ? "none" : "width .16s ease, transform .08s ease" };
    pointer-events: none;
    user-select: none;
  }
`;

export const ZoomControls = styled.div<{
  $bottomOffset?: number;
}>`
  position: absolute;
  left: 50%;
  bottom: ${({ theme, $bottomOffset = 0 }) => theme.spacing.x_medium + $bottomOffset }px;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xx_small }px;
  padding: ${({ theme }) => `${ theme.spacing.xxx_small }px ${ theme.spacing.xx_small }px`};
  border: 1px solid ${({ theme }) => theme.glass.border };
  border-radius: ${({ theme }) => theme.border_radius.medium }px;
  background-color: ${({ theme }) => theme.glass.elevated };
  backdrop-filter: ${({ theme }) => theme.glass.blur };
  box-shadow: ${({ theme }) => theme.shadow.default };
  transform: translateX( -50% );

  & > span {
    min-width: 48px;
    font-size: ${({ theme }) => theme.font_size.x_small }px;
    line-height: ${({ theme }) => theme.font_size.x_small }px;
    text-align: center;
    color: ${({ theme }) => theme.colors.text.default };
  }
`;
